import { useEffect, useState, useCallback } from "react";
import { createPortal } from "react-dom";
import { motion } from "framer-motion";
import { ArrowRight, X } from "@phosphor-icons/react";

const STEPS = [
  { sel: "[data-testid='cc-scope-toggle']", title: "See just your book",
    body: "Switch between every client and only the clients you're the account manager for." },
  { sel: "[data-testid='cc-trend-chart']", title: "Drill into a client",
    body: "Click any client in the trend legend (or a name in the worklist) to focus the whole cockpit on them." },
  { sel: "[data-testid='cc-briefing-bar']", title: "Monday morning briefing",
    body: "Choose who gets the weekly needs-attention email, or send yourself a test right now." },
];

export default function CommandCentreCoach({ enabled, replay = 0 }) {
  const [i, setI] = useState(0);
  const [rect, setRect] = useState(null);
  const [active, setActive] = useState(false);

  useEffect(() => {
    if (!enabled || localStorage.getItem("cc_coach_seen")) return;
    const t = setTimeout(() => setActive(true), 800);
    return () => clearTimeout(t);
  }, [enabled]);

  useEffect(() => {
    if (replay > 0) { setI(0); setActive(true); }
  }, [replay]);

  const measure = useCallback(() => {
    const el = document.querySelector(STEPS[i].sel);
    if (!el) { setRect(null); return; }
    el.scrollIntoView({ behavior: "smooth", block: "center" });
    setTimeout(() => {
      const r = el.getBoundingClientRect();
      setRect({ top: r.top, left: r.left, width: r.width, height: r.height });
    }, 280);
  }, [i]);

  useEffect(() => { if (active) measure(); }, [active, i, measure]);
  useEffect(() => {
    if (!active) return;
    const on = () => measure();
    window.addEventListener("resize", on);
    window.addEventListener("scroll", on, true);
    return () => { window.removeEventListener("resize", on); window.removeEventListener("scroll", on, true); };
  }, [active, measure]);

  const finish = () => { localStorage.setItem("cc_coach_seen", "1"); setActive(false); };
  const next = () => (i < STEPS.length - 1 ? setI(i + 1) : finish());

  if (!active) return null;
  const step = STEPS[i];
  const pad = 8;
  const ring = rect ? { top: rect.top - pad, left: rect.left - pad, width: rect.width + pad * 2, height: rect.height + pad * 2 } : null;
  const tipTop = rect ? Math.min(rect.top + rect.height + 14, window.innerHeight - 210) : 120;
  const tipLeft = rect ? Math.max(16, Math.min(rect.left, window.innerWidth - 344)) : 120;

  return createPortal(
    <div className="fixed inset-0 z-[9999]" data-testid="cc-coach">
      <div className="absolute inset-0" onClick={finish} style={ring ? {} : { background: "rgba(14,16,63,0.60)" }} />
      {ring && (
        <div className="absolute rounded-2xl pointer-events-none transition-all duration-300"
          style={{ ...ring, boxShadow: "0 0 0 9999px rgba(14,16,63,0.62)", outline: "2px solid #F37A24", outlineOffset: "2px" }} />
      )}
      <motion.div key={i} initial={{ opacity: 0, y: 10 }} animate={{ opacity: 1, y: 0 }}
        className="absolute w-[328px] bg-white rounded-2xl shadow-xl p-5"
        style={{ top: tipTop, left: tipLeft }} data-testid="cc-coach-card">
        <div className="flex items-center justify-between mb-1.5">
          <span className="overline text-orangeink">Tip {i + 1} of {STEPS.length}</span>
          <button onClick={finish} data-testid="cc-coach-skip" className="text-slate-400 hover:text-navy transition-colors"><X size={16} /></button>
        </div>
        <h4 className="font-heading font-bold text-navy text-base mb-1">{step.title}</h4>
        <p className="text-sm text-slate-600 leading-relaxed">{step.body}</p>
        <div className="flex items-center justify-between mt-4">
          <div className="flex gap-1.5">
            {STEPS.map((_, k) => <span key={k} className={`w-1.5 h-1.5 rounded-full transition-colors ${k === i ? "bg-orange" : "bg-slate-200"}`} />)}
          </div>
          <button onClick={next} data-testid="cc-coach-next"
            className="inline-flex items-center gap-1.5 bg-orange text-white text-sm font-medium px-4 py-2 rounded-lg hover:bg-navy transition-colors">
            {i < STEPS.length - 1 ? "Next" : "Got it"} <ArrowRight size={14} />
          </button>
        </div>
      </motion.div>
    </div>,
    document.body
  );
}
